import { useEffect, useMemo, useRef } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { useQuery } from '@tanstack/react-query';
import { fetchCountryRegions } from '@worldcoo/monolith/forms/regions';
import { NativeSelect, NativeSelectOptGroup, NativeSelectOption } from '../native-select';
import { cn } from '../../lib/utils';

export type NativeProvinceFieldTexts = {
	placeholder: string;
};

/**
 * Native so browsers autofill it (`address-level1` is the Spanish provincia). Option values are NAMES,
 * which autofill matches, and every province is listed while no region is chosen.
 */
export default function NativeProvinceField({
	label,
	country,
	region,
	className,
	texts,
	optional,
	autoComplete,
	onRegionChange,
}: {
	label: string;
	country?: string | undefined;
	region?: string | undefined;
	className?: string;
	texts: NativeProvinceFieldTexts;
	optional?: boolean;
	autoComplete?: string;
	onRegionChange?: (regionCode: string) => void;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name, optional);

	const prevCountryRef = useRef(country);
	useEffect(() => {
		if (prevCountryRef.current !== country) {
			prevCountryRef.current = country;
			field.handleChange('');
		}
	}, [country, field]);

	const { data: regions = [], isSuccess } = useQuery({
		queryKey: ['countries', country],
		queryFn: !country ? () => [] : () => fetchCountryRegions(country).then((data) => data ?? []),
	});

	const hasNoRegions = isSuccess && regions.length === 0;

	const nameByCode = useMemo(() => {
		const map = new Map<string, string>();
		for (const r of regions) for (const p of r.provinces) map.set(p.code, p.name);
		return map;
	}, [regions]);

	const selectedRegion = regions.find((r) => r.code === region);

	// The one list both the options and `flatOptions` are built from, so their order cannot drift.
	const listedRegions = useMemo(() => (selectedRegion ? [selectedRegion] : regions), [selectedRegion, regions]);

	// Not by name: "Merkez" names 38 Turkish provinces, and 42 of ~150 served countries repeat one.
	const flatOptions = useMemo(
		() => listedRegions.flatMap((r) => r.provinces.map((p) => ({ code: p.code, regionCode: r.code }))),
		[listedRegions],
	);

	// Index 0 is the placeholder; the rest follow document order across optgroups.
	const handleChange = (selectedIndex: number) => {
		const option = flatOptions[selectedIndex - 1];
		field.handleChange(option?.code ?? '');
		if (option) onRegionChange?.(option.regionCode);
	};

	return (
		<div className={cn('flex flex-col gap-2', className, (!country || hasNoRegions) && 'hidden')}>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<NativeSelect
				className="w-full"
				id={field.name}
				name={field.name}
				autoComplete={autoComplete}
				aria-invalid={hasFieldError(field.state.meta)}
				disabled={formReadOnly}
				value={nameByCode.get(field.state.value ?? '') ?? ''}
				onBlur={field.handleBlur}
				onChange={(event) => handleChange(event.target.selectedIndex)}
			>
				<NativeSelectOption value="">{texts.placeholder}</NativeSelectOption>
				{/* In `flatOptions` order; one region needs no optgroup repeating its name. */}
				{listedRegions.map((r) => {
					const provinces = r.provinces.map((province) => (
						<NativeSelectOption key={province.code} value={province.name}>
							{province.name}
						</NativeSelectOption>
					));
					if (selectedRegion) return provinces;
					return (
						<NativeSelectOptGroup key={r.code} label={r.name}>
							{provinces}
						</NativeSelectOptGroup>
					);
				})}
			</NativeSelect>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
